import type { ViewField } from '@lighthouse/core'
import {
    type DataSourceAbstract,
    type Field,
    type FieldADTValue,
    type FieldValue,
    type RecordLikeProtocol,
    type ViewFieldMember
} from '@lighthouse/core'
import { COLORS, fieldConvertValue, getAppointField, isIdsValue, PRESET_PALETTES } from '@lighthouse/shared'
import { eachDayOfInterval, format, getWeek, getYear, isAfter, isSameDay } from 'date-fns'
import { find } from 'rambda'

import type { TimeLineInfo } from './TimeLine/TimeLine.type'

/**
 * 获取第一个选项字段的选项颜色
 * @param schema
 * @returns
 */
const getSelectField = (scheduleColorField: string | undefined, columns: ViewField[]) => {
    // if (field) {
    //     const colors = field.select.options
    //         .map(item => item.color && COLORS.find(c => c.name === item.color)?.value)
    //         .filter(Boolean) as string[]
    //     if (colors.length > 0) {
    //         optionColors = colors.slice(0, maxLength)
    //     }
    // }

    // return optionColors
    return columns.find(item => item.type === 'select' && item.fieldId === scheduleColorField) as undefined | ViewFieldMember<'select'>
}

/**
 * 获取字段值
 * @param fieldValue
 * @param fieldSchema
 * @returns
 */
const getFieldValue = (fieldValue: FieldValue | undefined, fieldSchema: Field | undefined): string => {
    if (!fieldValue || !fieldSchema) {
        return ''
    }

    return fieldConvertValue({ ...fieldSchema, value: fieldValue.value } as FieldADTValue)
}

/** 检查字段是否有效 */
export const checkField = (key: string | undefined, schema: DataSourceAbstract['schema']): key is string =>
    !(!key || !Object.hasOwn(schema, key))

type GetTimeLinesConfig = {
    dataSource: DataSourceAbstract
    columns: ViewField[]
    titleField?: string
    startField?: string
    endField?: string
    color?: string
    colorMode?: 'follow' | 'custom'
    scheduleColorField?: string
}

/**
 * 计算需要生成的日程数据
 * @param records 记录
 * @param config 日程配置
 * @returns
 */
export const getTimeLines = (records: RecordLikeProtocol[], config: GetTimeLinesConfig) => {
    const {
        dataSource,
        columns,
        titleField,
        startField,
        endField,
        colorMode = 'follow',
        color = PRESET_PALETTES.purple,
        scheduleColorField
    } = config
    const { schema } = dataSource
    if (!checkField(titleField, schema) || !checkField(startField, schema)) {
        return []
    }

    // 获取第一个选项字段的选项颜色
    const selectField = getSelectField(scheduleColorField, columns)

    const createTimeField = getAppointField(dataSource, 'createdTime')

    return (
        records
            .map(record => {
                const title = getFieldValue(record.content[titleField], schema[titleField])
                const startDate = record.content[startField]?.value
                const endDate = endField ? record.content[endField]?.value : startDate

                if (
                    !startDate ||
                    !endDate ||
                    typeof startDate !== 'number' ||
                    typeof endDate !== 'number' ||
                    isAfter(startDate, endDate) ||
                    !createTimeField
                ) {
                    return false
                }

                const dates = eachDayOfInterval({ start: startDate, end: endDate })

                return {
                    id: record.id,
                    record,
                    index: 0,
                    title,
                    originDates: dates,
                    dates,
                    createdTime: record.content[createTimeField.id]?.value as number
                }
            })
            .filter(Boolean) as Omit<TimeLineInfo, 'color'>[]
    ).map(item => {
        // 12.29 经过克林指导获取第一个选项的label，然后通过label取字段的颜色
        if (colorMode === 'follow') {
            if (!selectField) {
                return {
                    ...item,
                    color: ''
                }
            }
            const labels = item.record.content?.[selectField.fieldId]?.value
            const label = isIdsValue(labels) ? labels?.[0] : undefined
            const selectColorName = find(option => option.label === label, selectField?.select.options || [])?.color ?? ''
            const selectColor = COLORS.find(item => item.name === selectColorName)?.value ?? ''
            return {
                ...item,
                color: selectColor
            }
        }

        return {
            ...item,
            color
            // color: colorMode === 'custom' ? color : optionColors[lastIndex === 0 ? 0 : index % lastIndex]
        }
    })
}

/**
 * 根据日程及当前日历生成 每周的日程数据
 * @param timeLines 所有日程
 * @param calendarDays 当前日历的单元格数据
 * @returns
 */
export const getSortedWeeks = (timeLines: TimeLineInfo[], calendarDays: Date[]) => {
    type WeekInfo = Record<string, TimeLineInfo[]>

    // 根据周分类
    const weekObj = timeLines.reduce<WeekInfo>((prev, next) => {
        return next.dates
            .filter(item => calendarDays.some(d => isSameDay(d, item)))
            .reduce((p, n) => {
                const week = `${getYear(n)}-${getWeek(n, { weekStartsOn: 0 })}`

                if (p[week]) {
                    const line = p[week].findIndex(i => i.id === next.id)
                    if (line === -1) {
                        p[week].push({ ...next, dates: [n] })
                    } else {
                        p[week][line].dates.push(n)
                    }
                } else {
                    p[week] = [{ ...next, dates: [n] }]
                }

                return prev
            }, prev)
    }, {})

    Object.keys(weekObj).forEach(k => {
        // 排序
        weekObj[k].sort((a, b) => a.originDates[0].getTime() - b.originDates[0].getTime())

        const virtualZone = [Array.from<boolean>({ length: 7 }).fill(false)]
        for (const line of weekObj[k]) {
            const start = line.dates[0]
            const end = line.dates[line.dates.length - 1]
            const startDay = Number(format(start, 'e', { weekStartsOn: 0 })) - 1
            const endDay = Number(format(end, 'e', { weekStartsOn: 0 })) - 1

            for (const [index, row] of virtualZone.entries()) {
                const isLast = index === virtualZone.length - 1
                // 该日期区间是否已有值插入
                const alreadyUsed = row.slice(startDay, endDay + 1).some(Boolean)

                if (alreadyUsed) {
                    // 如果最后一行也无法插入了，则插入到新行中
                    if (isLast) {
                        virtualZone.push(Array.from<boolean>({ length: 7 }).fill(false))
                    }
                    continue
                }

                // 记录行的使用情况
                for (let j = startDay; j < endDay + 1; j++) {
                    virtualZone[index][j] = true
                }
                // 设置最终插入到的行下标
                line.index = index

                break
            }
        }
    })

    return Object.entries(weekObj)
}
